import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, Menu, Phone, MessageCircle } from 'lucide-react';
import { TID } from '@/constants/testIds';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { LogoutDialog } from '@/components/LogoutDialog';
import { NotificationBell } from '@/components/NotificationBell';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { PWAInstallBanner } from '@/components/PWAInstallBanner';
import { FirstLoginModal } from '@/components/FirstLoginModal';

const LOGO_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_aac32766-5fa5-4a83-81c9-03bafeaebc9e/artifacts/bi1rz20k_loyerbook.png';

function SidebarInner({ links, testid, onNavigate }) {
  const { user } = useAuth();
  const [logoutOpen, setLogoutOpen] = useState(false);

  return (
    <div data-testid={testid} className="h-full w-full flex flex-col bg-[#0D1B2A] text-slate-100">
      <div className="px-5 py-6 border-b border-white/5 flex items-center gap-3">
        <div className="relative">
          <div className="absolute inset-0 lb-logo-pulse" />
          <img src={LOGO_URL} alt="LoyerBook" className="relative h-12 w-12 rounded-lg bg-white p-0.5 lb-logo-anim" />
        </div>
        <div>
          <div className="font-bold text-lg leading-tight tracking-tight">LoyerBook</div>
          <div className="text-[10px] uppercase tracking-widest text-[#7B94B0]">Carnet numérique</div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `lb-nav-item flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium ${
                isActive
                  ? 'bg-[#1677FF] text-white'
                  : 'text-slate-300 hover:bg-white/5 hover:text-white'
              }`
            }
          >
            <l.icon className="w-4 h-4 shrink-0" strokeWidth={2} />
            <span>{l.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/5 px-3 py-3 text-xs">
        <div className="mb-2 px-3 font-semibold uppercase tracking-wider text-slate-500">Assistance</div>
        {[
          { display: '+243 856 776 942', value: '243856776942' },
          { display: '+243 981 388 707', value: '243981388707' },
        ].map((phone) => (
          <div key={phone.value} className="flex items-center justify-between gap-2 rounded-md px-3 py-1.5 text-slate-300">
            <a href={`tel:+${phone.value}`} className="flex min-w-0 items-center gap-1.5 hover:text-white"><Phone className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{phone.display}</span></a>
            <a href={`https://wa.me/${phone.value}`} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${phone.display}`} className="text-emerald-400 hover:text-emerald-300"><MessageCircle className="h-4 w-4" /></a>
          </div>
        ))}
      </div>

      <div className="border-t border-white/5 p-3">
        <div className="px-3 py-2 mb-1">
          <div className="text-sm font-semibold truncate">{user?.full_name}</div>
          <div className="text-xs text-slate-400 truncate">{user?.email || user?.telephone}</div>
        </div>
        <button
          data-testid={TID.logout}
          onClick={() => setLogoutOpen(true)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
        >
          <LogOut className="w-4 h-4" /> Déconnexion
        </button>
      </div>
      <LogoutDialog open={logoutOpen} onOpenChange={setLogoutOpen} />
    </div>
  );
}

export function AppLayout({ links, testid, title, children }) {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <PWAInstallBanner />
      <FirstLoginModal user={user} />
      <div className="flex flex-1">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 sticky top-0 h-screen">
        <SidebarInner links={links} testid={testid} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar (mobile hamburger + title) */}
        <header className="lb-safe-x lg:hidden sticky top-0 z-30 flex min-h-14 items-center justify-between gap-2 border-b border-slate-200 bg-white">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[min(18rem,88vw)] border-r-0 bg-[#0D1B2A] p-0">
              <SidebarInner links={links} testid={`${testid}-mobile`} />
            </SheetContent>
          </Sheet>
          <div className="min-w-0 flex items-center gap-2">
            <img src={LOGO_URL} alt="LoyerBook" className="h-9 w-9 lb-logo-anim" />
            <span className="hidden truncate font-bold text-[#0D1B2A] min-[360px]:inline">LoyerBook</span>
          </div>
          <div className="flex items-center gap-1">
            <LanguageSwitcher compact testid="mobile-lang" />
            <NotificationBell />
          </div>
        </header>

        {/* Desktop top bar */}
        <div className="hidden lg:flex sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-slate-200 px-8 h-14 items-center justify-end gap-4">
          <LanguageSwitcher testid="desktop-lang" />
          <NotificationBell />
        </div>

        <main className="lb-safe-bottom flex-1 w-full max-w-full overflow-x-hidden px-3 py-4 sm:p-6 lg:p-8">
          {title && (
            <div className="mb-6">
              <h1 className="break-words text-xl font-bold tracking-tight text-[#0D1B2A] sm:text-3xl">{title}</h1>
            </div>
          )}
          {children}
        </main>
      </div>
      </div>
    </div>
  );
}
